import { mountFormContent } from '../form-content';
import { insertHost, keepHostAttached, readInlinePosition } from '../inline-host';
import type { GuideRenderContext } from '../types';
import { waitForElement } from '../wait-for-element';
import { BaseRenderer } from './base-renderer';

/**
 * Renderiza una guía `inline-form`: el MISMO formulario que `form` (campos →
 * props del usuario) pero EMBEBIDO en el flujo del DOM junto a un elemento
 * ancla, como `inline`. `step.selector` = ancla; `step.style.inlinePosition`
 * = dónde insertarse. El contenido compartido vive en `form-content.ts`.
 */
export class InlineFormRenderer extends BaseRenderer {
  async render(context: GuideRenderContext): Promise<void> {
    const step = context.guide.guideSteps[0];
    if (!step || (step.fields ?? []).length === 0) return;

    const selector = step.selector ?? context.guide.activationRules.selector;
    if (typeof selector !== 'string' || selector.length === 0) return;

    const anchor = await waitForElement(selector);
    if (!anchor) return;

    const { host, root } = this.createHost();
    // createHost adjuntó el host a <body>; lo movemos al flujo del DOM, junto
    // al ancla. `display:block` vence el `:host{all:initial}` (que sería inline).
    host.style.display = 'block';
    const position = readInlinePosition(step.style);
    insertHost(anchor, host, position);
    // El framework del host puede borrar el nodo al re-renderizar → watchdog.
    this.registerCleanup(keepHostAttached(host, selector, position));

    this.applyDesign(step.style);
    const doc = root.ownerDocument ?? document;

    const card = doc.createElement('div');
    card.className = 'veo-inline';
    mountFormContent(card, context, doc);
    root.appendChild(card);

    context.onInteraction({ guideId: context.guide.guideId, stepIndex: 0, action: 'shown' });
  }
}
